/**
 * components/Settings/AI/AISettingsPage.tsx
 * Orchestrator for AI settings — configured vs unconfigured states
 *
 * Checks whether the user has an AI provider configured and renders
 * either the setup wizard (unconfigured / switch-provider) or the
 * key management card (configured).
 *
 * Work Unit: WU02 - AI Settings Management
 * Task: T03 - Settings Integration
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Sparkles } from 'lucide-react';

// 2. Workspace packages
import { Button, Card, CardBody, Skeleton } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import { aiCredentialService } from '@/services/AICredentialService';
import { aiProviderManager } from '@/services/ai';

// 4. Relative imports
import { AIKeyManagement } from './AIKeyManagement';
import { AISetupWizard } from './AISetupWizard';

// ─── Types ──────────────────────────────────────────────────────────────────

/** View state for the AI settings page */
type AISettingsView = 'loading' | 'unconfigured' | 'wizard' | 'configured';

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the AI Settings Page orchestrator
 *
 * @see AISettingsPage
 */
export type AISettingsPageProps = Record<string, never>;

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * AI Settings Page Orchestrator
 *
 * Manages the top-level state machine for AI configuration:
 *
 * - **Loading**: Checks IndexedDB for existing credentials
 * - **Unconfigured**: Shows setup prompt with feature description
 * - **Wizard**: Renders `AISetupWizard` for initial setup or provider switch
 * - **Configured**: Renders `AIKeyManagement` with management actions
 *
 * @example
 * ```tsx
 * <AISettingsPage />
 * ```
 */
export const AISettingsPage: React.FC<AISettingsPageProps> = () => {
  // ─── State ────────────────────────────────────────────────────────────

  const [view, setView] = useState<AISettingsView>('loading');
  const [provider, setProvider] = useState<AIProviderType | null>(null);

  // ─── Refs ─────────────────────────────────────────────────────────────

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Load Configuration ───────────────────────────────────────────────

  const loadConfiguration = useCallback(async (): Promise<void> => {
    try {
      const [isConfigured, providers] = await Promise.all([
        aiProviderManager.isAnyProviderConfigured(),
        aiCredentialService.listProviders(),
      ]);

      if (!mountedRef.current) return;

      if (isConfigured && providers[0]) {
        setProvider(providers[0]);
        setView('configured');
      } else {
        setProvider(null);
        setView('unconfigured');
      }
    } catch {
      if (!mountedRef.current) return;
      setView('unconfigured');
    }
  }, []);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleSetupClick = useCallback((): void => {
    setView('wizard');
  }, []);

  const handleWizardComplete = useCallback((): void => {
    setView('loading');
    void loadConfiguration();
  }, [loadConfiguration]);

  const handleWizardCancel = useCallback((): void => {
    // Return to previous state based on whether we have a provider
    if (provider) {
      setView('configured');
    } else {
      setView('unconfigured');
    }
  }, [provider]);

  const handleConfigChange = useCallback((): void => {
    setView('loading');
    void loadConfiguration();
  }, [loadConfiguration]);

  const handleSwitchProvider = useCallback((): void => {
    setView('wizard');
  }, []);

  // ─── Render: Loading ──────────────────────────────────────────────────

  if (view === 'loading') {
    return (
      <Card className="w-full">
        <CardBody className="space-y-4 p-4 sm:p-6">
          <Skeleton className="h-6 w-48 rounded" />
          <Skeleton className="h-4 w-full rounded" />
          <Skeleton className="h-10 w-40 rounded-lg" />
        </CardBody>
      </Card>
    );
  }

  // ─── Render: Wizard ───────────────────────────────────────────────────

  if (view === 'wizard') {
    return (
      <AISetupWizard
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
      />
    );
  }

  // ─── Render: Configured ───────────────────────────────────────────────

  if (view === 'configured' && provider) {
    return (
      <AIKeyManagement
        provider={provider}
        onConfigChange={handleConfigChange}
        onSwitchProvider={handleSwitchProvider}
      />
    );
  }

  // ─── Render: Unconfigured ─────────────────────────────────────────────

  return (
    <Card className="w-full" aria-label="AI Assistant Setup">
      <CardBody className="flex flex-col items-center gap-4 p-6 text-center sm:p-8">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Bot className="size-6 text-primary" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Set Up AI Assistant</h3>
          <p className="max-w-md text-sm text-default-500">
            Connect an AI provider to get intelligent trade analysis,
            pattern recognition, and personalized insights for your trading journal.
          </p>
        </div>

        <Button
          color="primary"
          startContent={<Sparkles className="size-4" />}
          onPress={handleSetupClick}
          className="mt-2"
        >
          Set Up AI Assistant
        </Button>
      </CardBody>
    </Card>
  );
};
